import { useEffect, useState, useRef } from "react";
import { motion, useMotionValue } from "motion/react";
import { useNavigate } from "react-router";

const DRAG_BUFFER = 0;
const VELOCITY_THRESHOLD = 500;
const GAP = 16;
const SPRING_OPTIONS = { type: "spring", stiffness: 300, damping: 30 };

export default function NodeCarousal({
  activeNode,
  onNext,
  onPrev,
  chartNodes,
  baseWidth = 458,
  autoplay = true,
  autoplayDelay = 4000,
  pauseOnHover = true,
  loop = true,
  round = false,
}) {
  const containerPadding = 16;

  // ✅ Responsive item width logic
  const [responsiveWidth, setResponsiveWidth] = useState(baseWidth);
  useEffect(() => {
    const updateWidth = () => {
      const newWidth = window.innerWidth < 768 ? window.innerWidth : baseWidth;
      setResponsiveWidth(newWidth);
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [baseWidth]);

  const itemWidth = responsiveWidth - containerPadding * 2;
  const trackItemOffset = itemWidth + GAP;

  const carouselItems = loop ? [...chartNodes, chartNodes[0]] : chartNodes;
  const [currentIndex, setCurrentIndex] = useState(
    chartNodes.findIndex((node) => node.id === activeNode.id),
  );
  const x = useMotionValue(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const containerRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    const newIndex = chartNodes.findIndex((node) => node.id === activeNode.id);
    if (newIndex !== currentIndex) {
      setCurrentIndex(newIndex);
    }
  }, [activeNode, currentIndex, chartNodes]);

  useEffect(() => {
    if (pauseOnHover && containerRef.current) {
      const container = containerRef.current;
      const handleMouseEnter = () => setIsHovered(true);
      const handleMouseLeave = () => setIsHovered(false);
      container.addEventListener("mouseenter", handleMouseEnter);
      container.addEventListener("mouseleave", handleMouseLeave);
      return () => {
        container.removeEventListener("mouseenter", handleMouseEnter);
        container.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, [pauseOnHover]);

  useEffect(() => {
    if (autoplay && (!pauseOnHover || !isHovered)) {
      const timer = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev === chartNodes.length - 1 && loop) {
            return prev + 1;
          }
          if (prev === carouselItems.length - 1) {
            return loop ? 0 : prev;
          }
          return prev + 1;
        });
        onNext();
      }, autoplayDelay);
      return () => clearInterval(timer);
    }
  }, [
    autoplay,
    autoplayDelay,
    isHovered,
    loop,
    chartNodes.length,
    carouselItems.length,
    pauseOnHover,
    onNext,
  ]);

  const effectiveTransition = isResetting ? { duration: 0 } : SPRING_OPTIONS;

  const handleAnimationComplete = () => {
    if (loop && currentIndex === carouselItems.length - 1) {
      setIsResetting(true);
      x.set(0);
      setCurrentIndex(0);
      setTimeout(() => setIsResetting(false), 50);
    }
  };

  const handleDragEnd = (_, info) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    if (offset < -DRAG_BUFFER || velocity < -VELOCITY_THRESHOLD) {
      if (loop && currentIndex === chartNodes.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setCurrentIndex((prev) => Math.min(prev + 1, carouselItems.length - 1));
        onNext();
      }
    } else if (offset > DRAG_BUFFER || velocity > VELOCITY_THRESHOLD) {
      if (loop && currentIndex === 0) {
        setCurrentIndex(chartNodes.length - 1);
      } else {
        setCurrentIndex((prev) => Math.max(prev - 1, 0));
        onPrev();
      }
    }
  };

  const dragProps = loop
    ? {}
    : {
        dragConstraints: {
          left: -trackItemOffset * (carouselItems.length - 1),
          right: 0,
        },
      };

  return (
    <div
      ref={containerRef}
      className={`carousel-container ${round ? "round" : ""}`}
    >
      <motion.div
        className="carousel-track"
        drag="x"
        {...dragProps}
        style={{
          width: itemWidth,
          gap: `${GAP}px`,
          perspective: 1000,
          perspectiveOrigin: `${
            currentIndex * trackItemOffset + itemWidth / 2
          }px 50%`,
          x,
        }}
        onDragEnd={handleDragEnd}
        animate={{ x: -(currentIndex * trackItemOffset) }}
        transition={effectiveTransition}
        onAnimationComplete={handleAnimationComplete}
      >
        {carouselItems.map((item, index) => {
          return (
            <motion.div
              key={index}
              className={`carousel-item ${round ? "round" : ""}`}
              style={{
                width: itemWidth,
                height: round ? itemWidth : "100%",
                ...(round && { borderRadius: "50%" }),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div className={`carousel-item-header ${round ? "round" : ""}`}>
                <div className="carousel-item-title">{item.card.title}</div>
                <img
                  src={item.card.image}
                  alt={item.card.title}
                  className="carousel-item-image"
                />
              </div>
              <div className="carousel-item-content">
                <p className="carousel-item-description">
                  {item.card.description}
                </p>
                {/* Show See More button only for OpenLMIS */}
                {(item.id === "openLMIS" || item.id === "ai-as-practice") && (
                  <div className="openLmisbtn">
                    <button
                      className="see-more-btn"
                      onClick={() => {
                        if (item.id === "openLMIS") navigate("/openlmis");
                        else if (item.id === "ai-as-practice")
                          window.open("https://www.vitafi.ai/en", "_blank");
                      }}
                    >
                      See More
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </motion.div>
      <div className={`carousel-indicators-container ${round ? "round" : ""}`}>
        <div className="carousel-indicators">
          {chartNodes.map((_, index) => (
            <motion.div
              key={index}
              className={`carousel-indicator ${
                currentIndex % chartNodes.length === index
                  ? "active"
                  : "inactive"
              }`}
              animate={{
                scale: currentIndex % chartNodes.length === index ? 1.2 : 1,
              }}
              onClick={() => {
                setCurrentIndex(index);
                onNext();
              }}
              transition={{ duration: 0.15 }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
